import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Drawer, Input, Select, Table, Tooltip } from 'antd';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  FaBuilding,
  FaIdCard,
  FaPen,
  FaPlus,
  FaPhoneAlt,
  FaUsers,
} from 'react-icons/fa';
import { FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { DetenteurEntity } from '../../../../entities/detenteur.entity';
import { ORANGE, PRIMARY, YELLOW } from '../../../../shared/colors';
import { ExportFormatButton } from '../../../../shared/ExportFormatButton';
import { ExportFormat, getExportMetadata } from '../../../../shared/exportFiles';
import { Layout } from '../../../shared/Layout';
import { ClientForm } from '../../emissionTitre/components/ClientForm';
import { KYCForm } from '../../emissionTitre/components/KYCForm';
import { getDetenteurs } from '../../emissionTitre/network';
import { StatusPill, StatusTone } from '../../shared/StatusPill';
import { exportDetenteurData } from '../components/exportDataExcel';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  > .content {
    width: 100%;
    padding: 12px;
    color: var(--mt-text);
  }

  .client-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .client-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .client-hero p {
    max-width: 720px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.55;
  }

  .hero-actions,
  .toolbar-actions,
  .row-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .primary-action {
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    color: #07141c;
    font-family: Poppins-B;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
    font-family: Poppins-B;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .toolbar,
  .table-shell,
  .client-detail {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 82px;
    padding: 14px;
  }

  .kpi-card .label {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .kpi-card .value {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 24px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border: 1px solid rgba(255, 176, 0, 0.26);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 320px) 1fr minmax(320px, 380px) auto;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
  }

  .search-box {
    position: relative;
    min-width: min(100%, 360px);
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 11px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 8px;
    padding-left: 34px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .filter-tabs {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .advanced-filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(160px, 1fr));
    gap: 8px;
  }

  .advanced-filters .ant-select-selector {
    min-height: 38px;
    border-color: var(--mt-border) !important;
    border-radius: 999px !important;
    background: var(--mt-surface) !important;
    color: var(--mt-text) !important;
    box-shadow: none !important;
  }

  .advanced-filters .ant-select-selection-item,
  .advanced-filters .ant-select-selection-placeholder {
    color: var(--mt-text) !important;
  }

  .filter-tab {
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    padding: 8px 11px;
    background: var(--mt-surface);
    color: var(--mt-muted);
    cursor: pointer;
    font-family: Poppins-B;
    font-size: 12px;
  }

  .filter-tab.active {
    border-color: rgba(255, 176, 0, 0.55);
    color: var(--mt-warning);
    background: var(--mt-row-hover);
  }

  .client-workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 430px;
    gap: 12px;
    margin-top: 12px;
  }

  .table-shell {
    overflow: hidden;
  }

  .table-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 46px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    text-transform: uppercase;
  }

  .table-count {
    border: 1px solid rgba(255, 176, 0, 0.25);
    border-radius: 999px;
    padding: 5px 9px;
    color: #fff7e8;
    font-size: 11px;
    text-transform: none;
  }

  .client-name {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .client-name strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .client-name span,
  .muted {
    color: var(--mt-muted);
    font-size: 12px;
  }

  .action-button {
    border-color: var(--mt-border);
    border-radius: 999px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .action-button:hover {
    border-color: rgba(255, 176, 0, 0.55);
    color: var(--mt-warning);
  }

  .client-detail {
    min-height: 430px;
    overflow: hidden;
  }

  .client-detail-header {
    min-height: 46px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--mt-border);
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.12), transparent 42%),
      ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    text-transform: uppercase;
  }

  .client-detail-body {
    display: grid;
    gap: 12px;
    padding: 14px;
  }

  .client-avatar {
    display: grid;
    place-items: center;
    width: 54px;
    height: 54px;
    border-radius: 8px;
    color: #07141c;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    font-family: Poppins-B;
    font-size: 20px;
  }

  .detail-head {
    display: grid;
    grid-template-columns: 54px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    padding: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.08), transparent 42%),
      var(--mt-surface);
  }

  .detail-head h3 {
    margin: 0;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 18px;
    line-height: 1.2;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .detail-row {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px;
    background: var(--mt-surface);
  }

  .detail-row span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    text-transform: uppercase;
  }

  .detail-row strong {
    display: block;
    margin-top: 4px;
    color: var(--mt-text);
    font-family: Poppins-B;
    overflow-wrap: anywhere;
  }

  .detail-row.full {
    grid-column: 1 / -1;
  }

  .detail-section {
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    overflow: hidden;
    background: var(--mt-card);
  }

  .detail-section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 11px;
    border-bottom: 1px solid var(--mt-border);
    background: var(--mt-surface);
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 12px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .detail-section-title span {
    color: var(--mt-muted);
    font-family: Poppins-R;
    font-size: 11px;
    letter-spacing: 0;
    text-transform: none;
  }

  .detail-section-body {
    padding: 10px;
  }

  .score-track {
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: var(--mt-surface);
  }

  .score-fill {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, ${ORANGE}, ${YELLOW});
  }

  .missing-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }

  .missing-chip {
    border: 1px solid rgba(255, 176, 0, 0.28);
    border-radius: 999px;
    padding: 4px 8px;
    background: rgba(255, 176, 0, 0.08);
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 11px;
  }

  .detail-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .activity-list {
    display: grid;
    gap: 8px;
  }

  .activity-item {
    display: grid;
    grid-template-columns: 9px minmax(0, 1fr);
    gap: 9px;
    align-items: start;
    color: var(--mt-text);
  }

  .activity-dot {
    width: 9px;
    height: 9px;
    margin-top: 5px;
    border-radius: 999px;
    background: var(--mt-warning);
    box-shadow: 0 0 0 4px rgba(255, 176, 0, 0.12);
  }

  .activity-item strong {
    display: block;
    font-family: Poppins-B;
    font-size: 12px;
  }

  .activity-item span {
    display: block;
    margin-top: 2px;
    color: var(--mt-muted);
    font-size: 11px;
  }

  .empty-detail {
    display: grid;
    place-items: center;
    min-height: 310px;
    padding: 24px;
    color: var(--mt-muted);
    text-align: center;
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  .ant-table {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .ant-table-thead > tr > th {
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-family: Poppins-B;
    font-size: 11px;
    text-transform: uppercase;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--mt-border);
    color: var(--mt-text);
  }

  .odd-row {
    background-color: var(--mt-row-odd);
  }

  .even-row {
    background-color: var(--mt-row-even);
  }

  .odd-row:hover > td,
  .even-row:hover > td,
  .selected-row > td {
    background-color: var(--mt-row-hover) !important;
    cursor: pointer;
  }

  .ant-drawer-content {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .ant-drawer-header {
    border-bottom-color: var(--mt-border);
    background: ${PRIMARY};
  }

  .ant-drawer-title {
    color: ${YELLOW};
    font-family: Poppins-B;
  }

  @media (max-width: 760px) {
    > .content {
      padding: 10px;
    }

    .client-hero,
    .toolbar {
      align-items: stretch;
      display: flex;
      flex-direction: column;
    }

    .kpi-grid,
    .client-workspace,
    .advanced-filters {
      grid-template-columns: 1fr;
    }

    .hero-actions,
    .toolbar-actions {
      width: 100%;
    }

    .hero-actions .ant-btn,
    .toolbar-actions .ant-btn {
      flex: 1;
    }
  }
`;

type ClientFilter = 'all' | 'complete' | 'incomplete' | 'recent';

export const ClientPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDrawer1, setShowDrawer1] = useState(false);
  const [clients, setClients] = useState<DetenteurEntity[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<ClientFilter>('all');
  const [agencyFilter, setAgencyFilter] = useState<string>('all');
  const [managerFilter, setManagerFilter] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<DetenteurEntity>();
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    setIsLoading(true);
    getDetenteurs(connectedUser.id)
      .then(({ data }) => {
        setClients(data);
        setSelectedClient(data?.[0]);
      })
      .finally(() => setIsLoading(false));
  };

  const search = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const displayClients = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return clients.filter((client: any) => {
      const searchable = [
        client.nom,
        client.prenom,
        client.email,
        client.adresse,
        client.mobile,
        client.code_referent,
        getAgencyCode(client),
        getAgencyName(client),
        getManagerName(client),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !term || searchable.includes(term);
      const status = getClientFileStatus(client).type;
      const matchesAgency =
        agencyFilter === 'all' || getAgencyCode(client) === agencyFilter;
      const matchesManager =
        managerFilter === 'all' || getManagerName(client) === managerFilter;
      const isRecent = moment(client.created_at).isAfter(
        moment().subtract(30, 'days'),
      );
      const matchesFilter =
        activeFilter === 'all' ||
        (activeFilter === 'complete' && status === 'complete') ||
        (activeFilter === 'incomplete' && status !== 'complete') ||
        (activeFilter === 'recent' && isRecent);

      return matchesSearch && matchesFilter && matchesAgency && matchesManager;
    });
  }, [activeFilter, agencyFilter, clients, managerFilter, searchTerm]);

  const agencyOptions = useMemo(() => {
    const options = clients
      .map((client) => ({
        label: `${getAgencyName(client)} (${getAgencyCode(client)})`,
        value: getAgencyCode(client),
      }))
      .filter((option) => option.value && option.value !== '--');

    return getUniqueOptions(options);
  }, [clients]);

  const managerOptions = useMemo(() => {
    const options = clients
      .map((client) => ({
        label: getManagerName(client),
        value: getManagerName(client),
      }))
      .filter((option) => option.value && option.value !== '--');

    return getUniqueOptions(options);
  }, [clients]);

  const resetFilters = () => {
    setActiveFilter('all');
    setAgencyFilter('all');
    setManagerFilter('all');
    setSearchTerm('');
  };

  const clientStats = useMemo(() => {
    const complete = clients.filter(
      (client) => getClientFileStatus(client).type === 'complete',
    ).length;
    const agencies = new Set(clients.map(getAgencyCode).filter(Boolean));
    const managers = new Set(clients.map(getManagerName).filter(Boolean));
    const recent = clients.filter((client) =>
      moment(client.created_at).isAfter(moment().subtract(30, 'days')),
    ).length;

    return {
      total: clients.length,
      complete,
      incomplete: clients.length - complete,
      agencies: agencies.size,
      managers: managers.size,
      recent,
    };
  }, [clients]);

  const COLUMNS = [
    {
      title: 'Client',
      render: (cell: any, row: DetenteurEntity) => (
        <div className='client-name'>
          <strong>{getClientName(row)}</strong>
          <span>{(row as any).email || 'Email non renseigné'}</span>
        </div>
      ),
    },
    {
      title: 'Code client',
      dataIndex: 'code_referent',
      render: (cell: string) => <strong>{cell || '--'}</strong>,
    },
    {
      title: 'Contact',
      render: (cell: any, row: DetenteurEntity) => (
        <div className='client-name'>
          <span>{(row as any).mobile || '--'}</span>
          <span>{(row as any).adresse || 'Adresse non renseignée'}</span>
        </div>
      ),
    },
    {
      title: 'Agence',
      render: (cell: any, row: any) => (
        <div className='client-name'>
          <strong>{getAgencyName(row)}</strong>
          <span>{getAgencyCode(row)}</span>
        </div>
      ),
    },
    {
      title: 'Gestionnaire',
      render: (cell: any, row: any) => getManagerName(row),
    },
    {
      title: 'Dossier',
      render: (cell: any, row: any) => {
        const status = getClientFileStatus(row);
        return (
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        );
      },
    },
    {
      title: 'Date création',
      dataIndex: 'created_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    },
    {
      title: 'Actions',
      render: (cell: any, row: any) => (
        <div className='row-actions'>
          <Tooltip title='Modifier le client' color={YELLOW}>
            <Button
              className='action-button'
              icon={<FaPen />}
              onClick={(event) => {
                event.stopPropagation();
                setSelectedClient(row);
                setShowDrawer(true);
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const openClientDrawer = () => {
    setSelectedClient(undefined);
    setShowDrawer(true);
  };

  const exportClients = (format: ExportFormat) => {
    exportDetenteurData(clients, format, getExportMetadata(connectedUser));
  };

  const openEditDrawer = () => {
    if (!selectedClient) return;
    setShowDrawer(true);
  };

  const addClient = (cli: DetenteurEntity) => {
    setClients([...clients, cli]);
    setSelectedClient(cli);
  };

  return (
    <Layout>
      <Global styles={drawerStyles} />
      <Container>
        <div className='content'>
          <section className='client-hero'>
            <div>
              <span className='eyebrow'>Portefeuille client</span>
              <h1>Gestion de la clientèle</h1>
              <p>
                Pilotez les détenteurs, les coordonnées, les rattachements agence
                et les gestionnaires dans une vue opérationnelle claire.
              </p>
            </div>
            <div className='hero-actions'>
              <Button
                className='ghost-action'
                icon={<FiRefreshCcw />}
                onClick={fetchData}
              >
                Rafraîchir
              </Button>
              <ExportFormatButton
                className='ghost-action'
                onExport={exportClients}
              />
              <Button
                className='primary-action'
                icon={<FaPlus />}
                onClick={openClientDrawer}
              >
                Nouveau client
              </Button>
            </div>
          </section>

          <section className='kpi-grid'>
            <KpiCard
              icon={<FaUsers />}
              label='Clients total'
              value={clientStats.total}
            />
            <KpiCard
              icon={<FaIdCard />}
              label='Dossiers complets'
              value={clientStats.complete}
            />
            <KpiCard
              icon={<FaPhoneAlt />}
              label='Contacts incomplets'
              value={clientStats.incomplete}
            />
            <KpiCard
              icon={<FaBuilding />}
              label='Agences couvertes'
              value={clientStats.agencies}
            />
            <KpiCard
              icon={<FaPlus />}
              label='Créés 30 jours'
              value={clientStats.recent}
            />
          </section>

          <section className='toolbar'>
            <div className='search-box'>
              <FiSearch />
              <Input
                placeholder='Rechercher client, code, agence...'
                value={searchTerm}
                onChange={search}
              />
            </div>
            <div className='filter-tabs'>
              {CLIENT_FILTERS.map((filter) => (
                <button
                  className={`filter-tab ${
                    activeFilter === filter.value ? 'active' : ''
                  }`}
                  key={filter.value}
                  type='button'
                  onClick={() => setActiveFilter(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className='advanced-filters'>
              <Select
                value={agencyFilter}
                onChange={setAgencyFilter}
                options={[
                  { label: 'Toutes les agences', value: 'all' },
                  ...agencyOptions,
                ]}
              />
              <Select
                value={managerFilter}
                onChange={setManagerFilter}
                options={[
                  { label: 'Tous les gestionnaires', value: 'all' },
                  ...managerOptions,
                ]}
              />
            </div>
            <div className='toolbar-actions'>
              <Button className='action-button' onClick={resetFilters}>
                Réinitialiser
              </Button>
              <PulseLoader
                size={5}
                margin={2}
                color={YELLOW}
                loading={isLoading}
              />
            </div>
          </section>

          <section className='client-workspace'>
            <div className='table-shell'>
              <div className='table-title'>
                <span>Registre clientèle</span>
                <span className='table-count'>{displayClients.length} clients</span>
              </div>
              <Table
                columns={COLUMNS}
                dataSource={displayClients}
                rowClassName={(row, index) =>
                  `${index % 2 === 0 ? 'even-row' : 'odd-row'} ${
                    selectedClient?.id === row.id ? 'selected-row' : ''
                  }`
                }
                rowKey='id'
                size='small'
                loading={isLoading}
                scroll={{ x: 1100 }}
                pagination={false}
                onRow={(row) => ({
                  onClick: () => setSelectedClient(row),
                })}
              />
            </div>

            <aside className='client-detail'>
              <div className='client-detail-header'>Dossier client</div>
              {selectedClient ? (
                <div className='client-detail-body'>
                  <div className='detail-head'>
                    <div className='client-avatar'>
                      {getInitials(selectedClient)}
                    </div>
                    <div>
                      <h3>{getClientName(selectedClient)}</h3>
                      <span className='muted'>
                        {(selectedClient as any).code_referent || '--'}
                      </span>
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Synthèse
                      <span>{getClientCompleteness(selectedClient)}% complété</span>
                    </div>
                    <div className='detail-section-body'>
                      <div className='detail-grid'>
                        <div className='detail-row'>
                          <span>Dossier client</span>
                          <strong>
                            <StatusPill
                              tone={getClientFileStatus(selectedClient).tone}
                            >
                              {getClientFileStatus(selectedClient).label}
                            </StatusPill>
                          </strong>
                        </div>
                        <div className='detail-row'>
                          <span>Code référent</span>
                          <strong>
                            {(selectedClient as any).code_referent || '--'}
                          </strong>
                        </div>
                      </div>
                      <div className='score-track' style={{ marginTop: 10 }}>
                        <div
                          className='score-fill'
                          style={{
                            width: `${getClientCompleteness(selectedClient)}%`,
                          }}
                        />
                      </div>
                      <div className='missing-list'>
                        {getMissingClientFields(selectedClient).map((field) => (
                          <span className='missing-chip' key={field}>
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Identité
                      <span>Référence détenteur</span>
                    </div>
                    <div className='detail-section-body detail-grid'>
                      <div className='detail-row'>
                        <span>Nom</span>
                        <strong>{(selectedClient as any).nom || '--'}</strong>
                      </div>
                      <div className='detail-row'>
                        <span>Prénom</span>
                        <strong>{(selectedClient as any).prenom || '--'}</strong>
                      </div>
                      <div className='detail-row'>
                        <span>Nationalité</span>
                        <strong>
                          {(selectedClient as any).nationalite || '--'}
                        </strong>
                      </div>
                      <div className='detail-row'>
                        <span>Pièce d'identité</span>
                        <strong>
                          {(selectedClient as any).piece_identite
                            ? 'Disponible'
                            : 'Non jointe'}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Coordonnées
                      <span>Contact opérationnel</span>
                    </div>
                    <div className='detail-section-body detail-grid'>
                      <div className='detail-row full'>
                        <span>Email</span>
                        <strong>{(selectedClient as any).email || '--'}</strong>
                      </div>
                      <div className='detail-row'>
                        <span>Contact</span>
                        <strong>{(selectedClient as any).mobile || '--'}</strong>
                      </div>
                      <div className='detail-row'>
                        <span>Adresse</span>
                        <strong>
                          {(selectedClient as any).adresse || '--'}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Rattachement
                      <span>Agence et gestionnaire</span>
                    </div>
                    <div className='detail-section-body detail-grid'>
                      <div className='detail-row'>
                        <span>Agence</span>
                        <strong>{getAgencyName(selectedClient)}</strong>
                      </div>
                      <div className='detail-row'>
                        <span>Code agence</span>
                        <strong>{getAgencyCode(selectedClient)}</strong>
                      </div>
                      <div className='detail-row full'>
                        <span>Gestionnaire</span>
                        <strong>{getManagerName(selectedClient)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Suivi
                      <span>Historique administratif</span>
                    </div>
                    <div className='detail-section-body detail-grid'>
                      <div className='detail-row'>
                        <span>Création</span>
                        <strong>
                          {formatDate((selectedClient as any).created_at)}
                        </strong>
                      </div>
                      <div className='detail-row'>
                        <span>Dernière mise à jour</span>
                        <strong>
                          {formatDate((selectedClient as any).updated_at)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className='detail-section'>
                    <div className='detail-section-title'>
                      Activité client
                      <span>Journal synthétique</span>
                    </div>
                    <div className='detail-section-body activity-list'>
                      {getClientActivity(selectedClient).map((activity) => (
                        <div className='activity-item' key={activity.label}>
                          <span className='activity-dot' />
                          <div>
                            <strong>{activity.label}</strong>
                            <span>{activity.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='detail-actions'>
                    <Button className='primary-action' onClick={openEditDrawer}>
                      Modifier
                    </Button>
                    <Button
                      className='action-button'
                      onClick={() => {
                        if (!selectedClient) return;
                        setShowDrawer1(true);
                      }}
                    >
                      Voir KYC / AML
                    </Button>
                  </div>
                </div>
              ) : (
                <div className='empty-detail'>
                  Sélectionnez un client pour consulter son dossier.
                </div>
              )}
            </aside>
          </section>
        </div>
        <Drawer
          title={selectedClient ? 'Mettre à jour le client' : 'Nouveau client'}
          className='mt-client-drawer'
          placement='right'
          width='min(100vw, 720px)'
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <ClientForm
            clientToUpdate={selectedClient}
            setShowDrawer={setShowDrawer}
            getNewClient={addClient}
          />
        </Drawer>
        <Drawer
          title='Vérifier le KYC / AML'
          className='mt-client-drawer'
          placement='right'
          width='min(100vw, 720px)'
          onClose={() => setShowDrawer1(false)}
          open={showDrawer1}
          closeIcon={<MdClose size={20} />}
        >
          <KYCForm
            clientToUpdate={selectedClient}
            setShowDrawer={setShowDrawer1}
            getNewClient={addClient}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};

const KpiCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: number;
}) => (
  <div className='kpi-card'>
    <div>
      <span className='label'>{label}</span>
      <strong className='value'>{value}</strong>
    </div>
    <span className='icon'>{icon}</span>
  </div>
);

const drawerStyles = css`
  .mt-client-drawer .ant-drawer-content {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .mt-client-drawer .ant-drawer-header {
    min-height: 64px;
    border-bottom: 1px solid rgba(255, 176, 0, 0.22);
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.16), transparent 40%),
      ${PRIMARY};
  }

  .mt-client-drawer .ant-drawer-title {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 16px;
    text-transform: uppercase;
  }

  .mt-client-drawer .ant-drawer-close {
    color: #fff7e8;
  }

  .mt-client-drawer .ant-drawer-body {
    padding: 18px;
    background:
      radial-gradient(
        circle at 12% 0%,
        rgba(255, 79, 31, 0.06),
        transparent 25%
      ),
      var(--mt-bg);
  }

  .ant-select-dropdown {
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    background: var(--mt-card);
  }

  .ant-select-item {
    color: var(--mt-text);
  }

  .ant-select-item-option-selected,
  .ant-select-item-option-active {
    background: var(--mt-row-hover) !important;
    color: var(--mt-warning) !important;
  }
`;

const CLIENT_FILTERS: { label: string; value: ClientFilter }[] = [
  { label: 'Tous', value: 'all' },
  { label: 'Dossier complet', value: 'complete' },
  { label: 'Incomplets', value: 'incomplete' },
  { label: 'Récents', value: 'recent' },
];

const getClientFileStatus = (client: any) => {
  const requiredFields = getClientRequiredFields(client).map(
    (field) => field.value,
  );
  const completed = requiredFields.filter(
    (field) => field && field !== '--',
  ).length;

  if (completed === requiredFields.length) {
    return { label: 'Complet', type: 'complete' as const, tone: 'success' as StatusTone };
  }

  if (completed >= requiredFields.length - 2) {
    return { label: 'À compléter', type: 'review' as const, tone: 'warning' as StatusTone };
  }

  return { label: 'Incomplet', type: 'missing' as const, tone: 'danger' as StatusTone };
};

const getClientRequiredFields = (client: any) => [
  { label: 'Nom', value: client?.nom },
  { label: 'Prénom', value: client?.prenom },
  { label: 'Email', value: client?.email },
  { label: 'Contact', value: client?.mobile },
  { label: 'Adresse', value: client?.adresse },
  { label: 'Nationalité', value: client?.nationalite },
  { label: 'Agence', value: getAgencyCode(client) },
  { label: 'Gestionnaire', value: getManagerName(client) },
];

const getClientCompleteness = (client: any) => {
  const fields = getClientRequiredFields(client);
  const completed = fields.filter(
    (field) => field.value && field.value !== '--',
  ).length;

  return Math.round((completed / fields.length) * 100);
};

const getMissingClientFields = (client: any) => {
  const missing = getClientRequiredFields(client)
    .filter((field) => !field.value || field.value === '--')
    .map((field) => field.label);

  return missing.length ? missing : ['Dossier complet'];
};

const getClientActivity = (client: any) => [
  {
    label: 'Dossier créé',
    value: formatDate(client?.created_at),
  },
  {
    label: 'Dernière mise à jour',
    value: formatDate(client?.updated_at),
  },
  {
    label: 'Gestionnaire assigné',
    value: getManagerName(client),
  },
  {
    label: 'Contrôle KYC',
    value: client?.kyc?.status ? getReadableKycStatus(client.kyc.status) : 'Non soumis',
  },
];

const getReadableKycStatus = (status?: string) => {
  if (status === 'verified') return 'Vérifié';
  if (status === 'submitted') return 'Soumis';
  if (status === 'pending') return 'En attente';
  if (status === 'rejected') return 'Rejeté';
  return 'Non soumis';
};

const getUniqueOptions = (options: { label: string; value: string }[]) =>
  Array.from(new Map(options.map((option) => [option.value, option])).values());

const getClientName = (client: any) =>
  `${client?.nom || ''} ${client?.prenom || ''}`.trim() || 'Client sans nom';

const getInitials = (client: any) => {
  const nom = client?.nom?.[0] || '';
  const prenom = client?.prenom?.[0] || '';
  return `${nom}${prenom}`.toUpperCase() || 'CL';
};

const getAgencyName = (client: any) => client?.agence?.nom || '--';

const getAgencyCode = (client: any) => client?.agence?.code || '--';

const getManagerName = (client: any) =>
  `${client?.createur?.nom || ''} ${client?.createur?.prenom || ''}`.trim() ||
  '--';

const formatDate = (date?: string) =>
  date ? moment(date).format('DD/MM/YYYY') : '--';
